
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { PurchaseOrderModel } from '@/models/purchase_order';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import {
  buildInitialFinalizationCosts,
  landedPreviewForLine,
  canSubmitFinalization,
  buildFinalizationPayload,
  usesAverageCostMethod,
  displayIdentifier,
  hasAnyIdentifier,
  lineVariant,
} from '@/views/purchase-order/poCostFinalization';
import type { CostCalculationMethod } from '@/views/purchase-order/poCostFinalization';

/**
 * US-093 — finalise the costs on a received purchase order.
 *
 * The merchant receives stock the day it lands so it is sellable, then settles
 * the money here once the supplier's invoice arrives.
 *
 * All arithmetic and gating live in `poCostFinalization.ts` so they are covered
 * by tests rather than by a replica of this component's logic.
 */
@Component
export default class FinalisePoCostsDialog extends Vue {
  @Prop({ type: Boolean, default: false }) value!: boolean;
  @Prop({ type: Object, required: true }) purchaseOrder!: PurchaseOrderModel;
  @Prop({ type: Array, default: () => [] }) items!: any[];
  @Prop({ type: Array, default: () => [] }) receives!: any[];
  @Prop({ type: String, default: 'USD' }) currencyCode!: string;
  @Prop({ type: String, default: 'USD' }) storeCurrency!: string;
  @Prop({ type: Number, default: null }) exchangeRate!: number | null;

  private show = false;
  private saving = false;
  private errorMessage = '';

  private enteredCosts: { [key: number]: number } = {};
  private shippingCost = 0;
  private adjustmentAmount = 0;
  private invoiceNumber = '';
  private reason = '';
  private notes = '';

  private updateShopifyCost = true;
  private updateSupplierCost = true;
  private markAsComplete = false;

  // Same values and wording as CostTrackingForm on Receive Products, so the two
  // places a cost reaches Shopify read identically.
  private costCalculationMethod: CostCalculationMethod = 'latest';

  private costCalculationMethods: Array<{
    value: CostCalculationMethod; text: string; description: string;
  }> = [
    {
      value: 'latest',
      text: 'Latest Cost',
      description: 'Use the corrected cost on this purchase order',
    },
    {
      value: 'average',
      text: 'Weighted Average',
      description: 'Blend the corrected cost with current Shopify inventory',
    },
  ];

  @Watch('value', { immediate: true })
  onValueChange(val: boolean): void {
    this.show = val;
    if (val) { this.initFromPo(); }
  }

  @Watch('show')
  onShowChange(val: boolean): void {
    this.$emit('input', val);
  }

  private initFromPo(): void {
    const financial = (this.purchaseOrder as any)?.extra?.financial || {};
    this.enteredCosts = buildInitialFinalizationCosts(this.items);
    this.shippingCost = Number(financial.shipping_cost) || 0;
    this.adjustmentAmount = Number(financial.adjustment_amount) || 0;
    this.invoiceNumber = '';
    this.reason = '';
    this.notes = '';
    this.markAsComplete = false;
    this.errorMessage = '';
  }

  private get originalFinancial(): any {
    return (this.purchaseOrder as any)?.extra?.financial || {};
  }

  private get enteredFinancial(): any {
    return { shipping_cost: this.shippingCost, adjustment_amount: this.adjustmentAmount };
  }

  private get isForeignCurrency(): boolean {
    return !!this.currencyCode && this.currencyCode !== this.storeCurrency;
  }

  private get discountAmount(): number {
    return Number(this.originalFinancial.discount_calculated) || 0;
  }

  private get showAverageNote(): boolean {
    return usesAverageCostMethod(this.receives);
  }

  /**
   * Hidden when the PO carries no usable rate: the derivation would silently
   * fall back to a multiplier of 1 and label a supplier-currency amount as
   * store currency, right beside "this is what Shopify receives".
   */
  private get showLandedPreview(): boolean {
    return this.items.some((item) => this.landedPreview(item) !== null);
  }

  // A column nobody on this PO has a value for is just a column of dashes.
  private get showSkuColumn(): boolean {
    return hasAnyIdentifier(this.items, 'sku');
  }

  private get showSupplierSkuColumn(): boolean {
    return hasAnyIdentifier(this.items, 'supplierSku');
  }

  private displayIdentifier(value: string | null | undefined): string {
    return displayIdentifier(value);
  }

  private lineVariant(item: any): string {
    return lineVariant(item);
  }

  private landedPreview(item: any): number | null {
    return landedPreviewForLine(
      this.enteredCosts[Number(item.id)],
      this.currencyCode,
      this.storeCurrency,
      this.exchangeRate,
      item?.extra?.landed_cost_components,
    );
  }

  private get previewSubtotal(): number {
    return this.items.reduce((sum, item) => {
      const cost = Number(this.enteredCosts[Number(item.id)]) || 0;
      return sum + cost * (Number(item.orderedQuantity) || 0);
    }, 0);
  }

  private get previewGrandTotal(): number {
    const total = this.previewSubtotal
      - this.discountAmount
      + (Number(this.shippingCost) || 0)
      + (Number(this.adjustmentAmount) || 0);
    return CurrencyHelper.roundForCurrency(total, this.currencyCode);
  }

  private get canSubmit(): boolean {
    if (this.saving) { return false; }
    return canSubmitFinalization({
      items: this.items,
      entered: this.enteredCosts,
      financial: this.originalFinancial,
      enteredFinancial: this.enteredFinancial,
    });
  }

  private get currencyStep(): string {
    return CurrencyHelper.getCurrencyStep(this.currencyCode);
  }

  private formatCurrency(value: number | null | undefined): string {
    return CurrencyHelper.formatCurrencyWithCode(Number(value) || 0, this.currencyCode);
  }

  private formatStoreCurrency(value: number | null): string {
    if (value === null) { return '—'; }
    return CurrencyHelper.formatCurrencyWithCode(value, this.storeCurrency);
  }

  private async save(): Promise<void> {
    if (!this.canSubmit) { return; }

    this.saving = true;
    this.errorMessage = '';

    try {
      const payload = buildFinalizationPayload({
        items: this.items,
        entered: this.enteredCosts,
        financial: this.originalFinancial,
        enteredFinancial: this.enteredFinancial,
        invoiceNumber: this.invoiceNumber,
        reason: this.reason,
        notes: this.notes,
        updateShopifyCost: this.updateShopifyCost,
        costCalculationMethod: this.costCalculationMethod,
        updateSupplierCost: this.updateSupplierCost,
        markAsComplete: this.markAsComplete,
      });

      const result = await this.purchaseOrder.finaliseCosts(payload);

      this.$emit('finalised', result);
      this.show = false;
    } catch (error: any) {
      // warbler's http layer returns the body raw, so error.response.data can be
      // a string. Reading .message off it would silently give undefined.
      const data = error?.response?.data;
      const fromBody = typeof data === 'string' ? data : data?.message;
      this.errorMessage = fromBody || error?.message || 'Could not finalise the costs.';
    } finally {
      this.saving = false;
    }
  }

  private close(): void {
    if (this.saving) { return; }
    this.show = false;
  }
}
